"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { ArrowRight, Code2, Wrench } from "lucide-react";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { buildLocalizedPath, stripLanguageFromPath, type Language } from "@/lib/i18n";
import { useTranslations } from "@/components/i18n/use-translations";
import { buildSegmentDocumentTitle } from "@/lib/document-title";
import type { SiteTranslations } from "@/lib/translations";

const ROUTE_TITLES: Record<string, (messages: SiteTranslations) => string> = {
    "/": (messages) => messages.home.heroSubtitle,
    "/tools": (messages) => messages.tools.page.title,
    "/tools/deconstruct-url": (messages) => messages.tools.tabs.urlDeconstructor.title,
    "/tools/ip-inspector": (messages) => messages.tools.tabs.ipInspector.title,
    "/tools/defang-url": (messages) => messages.tools.tabs.defang.title,
    "/tools/ioc-extractor": (messages) => messages.tools.tabs.iocExtractor.title,
    "/tools/base64-lab": (messages) => messages.tools.tabs.base64Lab.title,
    "/tools/hash-lab": (messages) => messages.tools.tabs.hashLab.title,
    "/tools/email-header-analyzer": (messages) => messages.tools.tabs.emailHeaderAnalyzer.title,
    "/tools/jwt-decoder": (messages) => messages.tools.tabs.jwtDecoder.title,
    "/tools/timestamp-converter": (messages) => messages.tools.tabs.timestampConverter.title,
    "/tools/cidr-calculator": (messages) => messages.tools.tabs.cidrCalculator.title,
    "/faq": (messages) => messages.faq.title,
    "/developers": (messages) => messages.developers.title,
    "/terms": (messages) => messages.terms.title,
    "/donate": (messages) => messages.donate.title,
    "/submit-feed": (messages) => messages.submitFeed.title,
};

export default function Component({ lang }: { lang: Language }) {
    const { messages } = useTranslations();
    const pathname = usePathname();

    useEffect(() => {
        const stripped = stripLanguageFromPath(pathname);
        const titleFn = ROUTE_TITLES[stripped] ?? ROUTE_TITLES["/"];
        const segment = titleFn(messages);
        document.title = buildSegmentDocumentTitle(segment, messages.meta.siteName);
    }, [messages, pathname]);

    return (
        <nav className="sticky top-0 z-50 bg-linear-to-r from-slate-950/90 via-slate-900/90 to-slate-950/90 backdrop-blur-md border-b border-slate-800/50 text-white w-full transition-shadow duration-300 hover:shadow-lg hover:shadow-cyan-600/10">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
                <Link href={buildLocalizedPath("/", lang)} className="flex items-center gap-2 sm:gap-3 group shrink-0" prefetch={false} aria-label="ScaMatrix home">
                    <Image
                        src="/icon.svg"
                        alt=""
                        width={48}
                        height={48}
                        className="w-9 h-9 sm:w-12 sm:h-12 group-hover:scale-110 transition-transform"
                        priority
                    />
                    <span className="title-scamatrix text-xl sm:text-2xl font-semibold text-gray-100 group-hover:text-white transition-colors">
                        Sca<span className="bg-linear-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">Matrix</span>
                    </span>
                </Link>

                <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                    <LanguageSwitcher currentLang={lang} />
                    <Link
                        href={buildLocalizedPath("/tools", lang)}
                        aria-label={messages.nav.freeTools}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-700/80 px-3 py-1.5 text-sm font-medium text-gray-300 transition-colors hover:border-cyan-500/60 hover:text-cyan-200"
                    >
                        <Wrench className="h-4 w-4" aria-hidden="true" />
                        <span className="hidden sm:inline">{messages.nav.freeTools}</span>
                    </Link>
                    <Link
                        href={buildLocalizedPath("/developers", lang)}
                        className="group inline-flex shrink-0 items-center gap-2 rounded-full border border-cyan-400/40 bg-linear-to-b from-slate-800 to-slate-900 px-3 sm:px-4 py-1.5 text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_0_0_1px_rgba(34,211,238,0.08),0_6px_20px_-8px_rgba(34,211,238,0.55)] transition-all hover:border-cyan-300/70 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_0_0_1px_rgba(34,211,238,0.2),0_8px_24px_-8px_rgba(34,211,238,0.75)]"
                    >
                        <Code2 className="h-4 w-4 text-cyan-300" aria-hidden="true" />
                        <span>{messages.footer.developers}</span>
                        <ArrowRight className="hidden sm:block h-3.5 w-3.5 text-cyan-300/80 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 group-hover:rtl:-translate-x-0.5" aria-hidden="true" />
                    </Link>
                </div>
            </div>
        </nav>
    );
}
